import {
  faAnglesLeft,
  faAnglesRight,
  faCheck,
  faExclamation,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { SeverityLevel } from '@microsoft/applicationinsights-web'
import { Badge } from 'primereact/badge'
import { ConfirmDialog } from 'primereact/confirmdialog'
import { Dialog } from 'primereact/dialog'
import { Timeline } from 'primereact/timeline'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import { CertificateDocumentStatus } from '../../../../enums/CertificateDocumentStatus'
import { CertificateDocumentType } from '../../../../enums/CertificateDocumentType'
import { reloadCertificateList } from '../../../../features/certificateList/certificateListSlice'
import {
  selectAutotimestampTask,
  selectCertDetailFormValues,
  selectCertDetailValues,
  selectHasCertificateProductStandard,
  selectHasEmptyProductStandardRow,
  selectIsRPAttachmentValid,
  selectModelsProductStandardsUpdates,
  selectProdDetailValues,
  selectSchemeEcmType,
  selectStandardValue,
  selectValues,
  selectIsModelOnEdit,
  selectIsCo,
  selectSkipCoDecisioning,
  updateAutoTimestampTask,
  updateCertDetailsFormValues,
  updateCertificateNumber,
  updateCertificateScheme,
  updateCopyPartyInformation,
  updateHasCertificateProductStandard,
  updateHasEmptyProductStandardRow,
  updateHasExistingModels,
  updateIsTempStandardOptionLoaded,
  updateLineDetails,
  updateModelList,
  updateOrderLineNumbers,
  updateProdDetailsAttachments,
  updateProdDetailsModelList,
  updateProductCategoryStandards,
  updateSecondaryStandardOptions,
  updateSecondaryStandardValues,
  updateSelectedOrderLineNumber,
  updateSelectedProjectDetails,
  updateStandardOptions,
  updateStandardValues,
  updateTempSecondaryStandardOptions,
  updateTempStandardOptions,
  updateIsModelOnEdit,
  updateSkipCoDecisioning,
  updateCopyState,
  selectIsStandardRequired,
  selectIsAddNewCertFromParams,
  updateIsAddNewCertFromParams,
  updateAssetDocuments,
  selectAssetDocuments,
  selectCertificateSchemeFromFormValues as selectedCertificateScheme,
  selectCertificateSubprogram,
  clearAssetDocuments,
  applyAssetDocumentSaveResults,
  selectCertificateAction,
  updateCertificateAction,
  selectCertDetailFlatAttribute,
  updateServiceRequestNumber,
  updateToggleServiceRequest,
  resetProductRatings,
} from '../../../../features/createCertificate/createCertificateSlice'
import useToastr from '../../../../hooks/useToastr'
import CertificateListService from '../../../../services/CertificateListService'
import ProjectDetailsService from '../../../../services/ProjectDetailsService'
import { appInsights } from '../../../../services/appInsights'
import CloseIcon from '../../../../shared/close-icon/CloseIcon'
import { formatDateToISO, formatDateToISOV2 } from '../../../../utils/helpers/date.helpers'
import { parseToString } from '../../../../utils/helpers/string-format.helper'
import './AddNewCertificate.scss'
import AddNewCertDetails from './add-new-cert-details/AddNewCertDetails'
import AddNewCertProdDetails from './add-new-cert-prod-details/AddNewCertProdDetails'
import ProjectOrderDetails from './project-order-details/ProjectOrderDetails'

import clsx from 'clsx'
import ExpandIcon from '../../../../shared/expand-icon/ExpandIcon'
import { isObjectInvalid, objectToFormData } from '../../../../utils/helpers/object.helpers'
import { chunkArray } from '../../../../utils/helpers/array.helpers'
import classes from './AddNewCertificate.module.scss'
import TestReportMatchedFoundPopup from './test-report-matched-found-popup/TestReportMatchedFoundPopup'
import { useTestReportValidator } from './test-report-matched-found-popup/useTestReportValidator'
import useRefinerEvent from '../../hooks/useRefinerEvent'
import { SchemeEcmType } from '../../../../enums/SchemeEcmType'
import numeral from 'numeral'
import { AddAttachmentsRPUtils } from './rp-library/shared/utils/AddAttachmentsRPUtils'
import {
  CERTIFICATE_MARK_WITHOUT_IMAGE_COLUMNNS,
  CERTIFICATE_SCHEME,
} from '../../../../utils/constants/certificate.constants'
import {
  PendingWithdrawalSuccessHeader,
  PendingWithdrawalSuccessMessage,
  WithdrawalAttachmentUploadingSuccessHeader,
  WithdrawalAttachmentUploadingSuccessMessage,
} from '../certificate-details/shared/constants/CertificateUploadToastMessages'

const attachmentUploadBatchSize = 20

const AddNewCertificate = props => {
  const logPrefix = `AddNewCertificate - ${uuidv4()}`

  const {
    displayAddNewCertModal,
    dialogFuncMap,
    isEditCert,
    isModifyCert,
    setIsModifyCert,
    hierarchyId,
    editData,
    localData,
    hasRevisions,
    certificateId,
    certificateScheme,
    revisionNumber,
    setShowCertificateDetails,
    passedCurrentStep,
    setPassedCurrentStep,
    setIsOpenAutotimestampModal,
    customModalHeader,
    certificateStatus,
    inputAttachments,
    setAttachments,
    rpAttachments,
    setRPAttachments,
    uniqueCertificateId,
    isCopyCertificate,
  } = props

  const dispatch = useDispatch()
  const projectDetailsValues = useSelector(selectValues)
  const productDetailsValues = useSelector(selectProdDetailValues)
  const certificateDetailsValues = useSelector(selectCertDetailValues)
  const certificateDetailFormValues = useSelector(selectCertDetailFormValues)
  const modelsProductStandardsUpdatesValues = useSelector(selectModelsProductStandardsUpdates)
  const hasEmptyProductStandardRow = useSelector(selectHasEmptyProductStandardRow)
  const standardValues = useSelector(selectStandardValue)

  const hasCertificateProductStandard = useSelector(selectHasCertificateProductStandard)
  const isCo = useSelector(selectIsCo)
  const isSkipCoDecisioning = useSelector(selectSkipCoDecisioning)
  const refinerEvent = useRefinerEvent()

  const isEmptyStandard = hasEmptyProductStandardRow && hasCertificateProductStandard

  const tasks = useSelector(selectAutotimestampTask)
  const isRPAttachmentValid = useSelector(selectIsRPAttachmentValid)
  const schemeEcmType = useSelector(selectSchemeEcmType)
  const isModelOnEdit = useSelector(selectIsModelOnEdit)
  const isStandardRequired = useSelector(selectIsStandardRequired)

  const isAddNewCertFromParams = useSelector(selectIsAddNewCertFromParams)

  const tempAssetDocu = useSelector(selectAssetDocuments)
  const certifcateSchemeFromValue = useSelector(selectedCertificateScheme)
  const certificateSubprogram = useSelector(selectCertificateSubprogram)
  const certificateAction = useSelector(selectCertificateAction)
  const certificateFlatAttributes = useSelector(selectCertDetailFlatAttribute)

  const isAnzCertificateScheme =
    certifcateSchemeFromValue?.toLocaleLowerCase() ===
    CERTIFICATE_SCHEME.ANZ_CERTIFICATION_SCHEME.toLocaleLowerCase()

  const withoutImageColumns = CERTIFICATE_MARK_WITHOUT_IMAGE_COLUMNNS.includes(
    certificateSubprogram?.toLocaleLowerCase() ?? ''
  )
  const [isShowCertSchemeDropdown, setIsShowCertSchemeDropdown] = useState(true)

  const [formValues, setFormValues] = useState(editData ?? {})
  const steppedBackgroundColor = '#0088cc'
  const steppedTextColor = '#fff'
  const unSteppedBackgroundColor = '#d7dbe4'
  const unSteppedTextColor = '#d7dbe4'
  const invalidStepBackgroundColor = '#ffa700'
  const invalidStepTextColor = '#fff'
  const modalSteps = [{ number: 1 }, { number: 2 }, { number: 3 }]

  const [isCancelConfirmDialogShow, setIsCancelConfirmDialogShow] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [schemeList, setSchemeList] = useState([])
  const [isValidStep, setIsValidStep] = useState({
    2: true,
    3: true,
  })
  const [isDisableContinue, setIsDisableContinue] = useState(true)
  const [isDisableDraftSave, setIsDisableDraftSave] = useState(true)
  const [isDisableSaveAndAttach, setIsDisableSaveAndAttach] = useState(true)
  const [isSavingCertificate, setIsSavingCertificate] = useState(false)
  const [isAttachmentsValid, setIsAttachmentsValid] = useState(true)
  const [selectedScheme, setSelectedScheme] = useState(null)

  const [initialProductDetailsValuesLocal, setInitialProductDetailsValuesLocal] = useState(null)
  const [initialCertificateDetailFormValuesLocal, setInitialCertificateDetailFormValuesLocal] =
    useState(null)
  const [initialAttachmentValuesLocal, setInitialAttachmentValuesLocal] = useState(null)
  const [outputAttachmentValues, setOutputAttachmentValues] = useState(null)
  const [outputAttachmentRPValues, setOutputAttachmentRPValues] = useState([])

  const [testReportRows, setTestReportRows] = useState(null)

  const testReportsValidatorHook = useTestReportValidator()
  const { showSuccess, showError, showInfo } = useToastr()

  const [footerActionsDisabled, setFooterActionsDisabled] = useState(false)
  const [formAttributesWithError, setFormAttributesWithError] = useState(null)
  const [formValidityMessages, setFormValidityMessages] = useState({})
  const [scrollIntoBlankRequiredElement, setScrollIntoBlankRequiredElement] = useState(null)

  const [isModelInRowEditMode, setIsModelInRowEditMode] = useState(false)
  const [isActivateConfirmDialogShow, setIsActivateConfirmDialogShow] = useState(false)
  const [isWithdrawConfirmDialogShow, setIsWithdrawConfirmDialogShow] = useState(false)
  const [hasCertificateBeenActive, setHasCertificateBeenActive] = useState(false)

  const [dialogMaximized, setDialogMaximized] = useState(true)

  const [isCertificateHaveStandard, setIsCertificateHaveStandard] = useState(null)
  const [isLastStepDirty, setIsLastStepDirty] = useState(false)
  const [certificateNumber, setCertificateNumber] = useState(null)
  const [isAutoGenerate, setIsAutoGenerate] = useState(false)

  useEffect(() => {
    if (isEditCert || isModifyCert) dispatch(updateIsAddNewCertFromParams(false))
  }, [])

  const modalHeader = {
    1: 'Add New Certificate - Project Details',
    2: 'Add New Certificate - Certificate Details',
    3: 'Add New Certificate - Attachments',
  }

  let topView = useRef < HTMLDivElement > null

  useEffect(() => {
    if (!certificateId) {
      setHasCertificateBeenActive(false)
      return
    }

    const hasCertificateBeenActive = async () => {
      const result = await CertificateListService.getHasCertificateBeenActive(certificateId)
      setHasCertificateBeenActive(result.data)
    }

    hasCertificateBeenActive()
  }, [certificateId])

  useEffect(() => {
    if (displayAddNewCertModal) {
      getSchemeList()
      //when modals open, clear standard options
      dispatch(updateStandardOptions(null))
    } else {
      dispatch(updateCopyPartyInformation(false))
    }

    setDialogMaximized(true)
  }, [displayAddNewCertModal])

  /**
   * This function handles RP Attachments on Modify/Edit
   * it updates the outputAttachmentsRPValue which is manipulated
   * throughout AddNewCertificate.js for RP Attachments state.
   */
  const handleGetRPAttachments = useCallback(() => {
    if (!projectDetailsValues) return

    let initialRPAttachments = rpAttachments

    if (!initialRPAttachments) initialRPAttachments = []

    setOutputAttachmentRPValues(initialRPAttachments)
  }, [rpAttachments, projectDetailsValues])

  const updateRPAttachmentOnParent = updatedRPAttachments => {
    if (!(isModifyCert || isEditCert)) return

    setRPAttachments(updatedRPAttachments)
  }

  useEffect(() => {
    if (handleGetRPAttachments) handleGetRPAttachments()
    return () => {}
  }, [handleGetRPAttachments])

  const onHide = (name, isSaved = false) => {
    window.parent.postMessage({ type: 'UNSAVED_CHANGES', value: false }, '*')
    setCurrentStep(1)
    setFormAttributesWithError(null)

    if (setPassedCurrentStep !== undefined) {
      setPassedCurrentStep(null)
    }

    dialogFuncMap[`${name}`](false)
    dispatch(updateSelectedProjectDetails(null))
    dispatch(updateLineDetails([]))
    dispatch(updateOrderLineNumbers([]))
    dispatch(updateSelectedOrderLineNumber(null))
    dispatch(updateProdDetailsAttachments(null))
    dispatch(updateCertificateNumber(null))
    dispatch(updateProdDetailsModelList(null))
    dispatch(updateCertificateScheme(null))
    if (!isAddNewCertFromParams) dispatch(updateModelList([]))

    dispatch(updateServiceRequestNumber(null))
    dispatch(updateStandardOptions(null))
    dispatch(updateSecondaryStandardOptions(null))
    dispatch(updateHasExistingModels(false))
    dispatch(updateCertDetailsFormValues({}))
    setInitialProductDetailsValuesLocal(null)
    setInitialCertificateDetailFormValuesLocal(null)
    setInitialAttachmentValuesLocal(null)
    setSelectedScheme(null)
    setIsDisableSaveAndAttach(true)
    setIsLastStepDirty(false)
    dispatch(updateStandardValues(null))
    dispatch(updateSecondaryStandardValues(null))
    dispatch(updateHasEmptyProductStandardRow(true))
    dispatch(updateHasCertificateProductStandard(true))
    dispatch(updateProductCategoryStandards(null))
    dispatch(updateIsTempStandardOptionLoaded(false))
    dispatch(updateTempStandardOptions(null))
    dispatch(updateTempSecondaryStandardOptions(null))
    dispatch(updateIsModelOnEdit(false))
    dispatch(updateSkipCoDecisioning(false))
    dispatch(
      updateCopyState({
        certificateScheme: false,
        certificateNumber: false,
        models: false,
        partyInformation: false,
      })
    )
    dispatch(clearAssetDocuments())
    dispatch(updateCertificateAction({ isWithdraw: false }))
    if (isSaved) {
      if (setShowCertificateDetails !== undefined) {
        setShowCertificateDetails(false)
      }

      if (setIsOpenAutotimestampModal !== undefined && tasks.length > 0) {
        setIsOpenAutotimestampModal(true)
      }
      dispatch(reloadCertificateList(true))
    } else {
      dispatch(updateAutoTimestampTask([]))
    }
    setOutputAttachmentRPValues([])
    dispatch(resetProductRatings())

    setIsValidStep({
      2: true,
      3: true,
    })

    dispatch(updateToggleServiceRequest())
  }

  const onBack = () => {
    setCurrentStep(prevState => {
      let newState = prevState
      if (prevState > 1) {
        newState = prevState - 1
      }

      return newState
    })
  }

  const validateTestReport = async () => {
    var duplicateTestReportRows = outputAttachmentValues[CertificateDocumentType.TestReport].filter(
      testReportDocument =>
        testReportDocument.status == CertificateDocumentStatus.TestReportDuplicate
    )

    if (duplicateTestReportRows.length > 0) {
      setTestReportRows(duplicateTestReportRows)
      testReportsValidatorHook.setTestReportMatchedFoundPopupVisible(true)
    } else {
      onFinish(true)
    }
  }

  const onSaveAssetImage = async () => {
    const result = await CertificateListService.manageDocuments(tempAssetDocu)

    const successItems = result.data?.flatMap(r => r.data?.results?.successArray ?? []) ?? []

    const errorItems = result.data?.flatMap(r => r.data?.results?.errorArray ?? []) ?? []

    dispatch(
      applyAssetDocumentSaveResults({
        successItems,
      })
    )

    return errorItems?.length > 0 || !result?.isSuccessful
  }

  const onFinish = async (isAutoActivated = false) => {
    testReportsValidatorHook.setTestReportMatchedFoundPopupVisible(false)

    setIsSavingCertificate(true)

    if (isAnzCertificateScheme && tempAssetDocu.length > 0 && !withoutImageColumns) {
      const hasNoErrorUploadAssetImage = await onSaveAssetImage()

      if (hasNoErrorUploadAssetImage) {
        showError('Image upload failed', 'Upload failed. Please try again.')
        setIsSavingCertificate(false)
        return
      }
    }

    let newAttributeFlatList = JSON.parse(
      JSON.stringify(certificateDetailsValues.flatAttributeList)
    )

    newAttributeFlatList.forEach(item => {
      const latestValue =
        isAutoGenerate && item.attribute_unique_name === 'certificate_certificateName'
          ? certificateNumber
          : certificateDetailsValues.formValues[item.attribute_unique_name]
      item.value = latestValue

      if (!item) return

      switch (item.data_type) {
        case 'date':
          if (
            !latestValue ||
            latestValue === '' ||
            (Array.isArray(latestValue) && latestValue.length === 0)
          ) {
            item.value = ''
          } else {
            item.value = formatDateToISOV2(latestValue)
          }

          return
        case 'text':
          if (!item.is_allow_search) break
          const maxValue = numeral(item.max_value).value()
          if (maxValue <= 0 && item.max_value !== 'n') break
          if (!Array.isArray(latestValue)) break

          item.value = latestValue.filter(Boolean)
          return
      }
    })

    let attachments = []

    // reverted the bugfix for bug 36246
    // const updatedRevisionNumber = newAttributeFlatList.find(
    //   (x) => x.attribute_unique_name == "certificate_revisionNumber"
    // ).value;

    // insert save asset images here

    const writebackData = {
      projectDetailsValues: projectDetailsValues.selectedProjectDetails,
      newAttributeFlatList: newAttributeFlatList,
      modelList: productDetailsValues.modelList,
      remarks: productDetailsValues.remarks,
      attachments: attachments,
      isAutoActivated: isAutoActivated,
      revisionNumber: revisionNumber, //updatedRevisionNumber ?? revisionNumber // reverted the bugfix for bug 36246,
      certificateId: certificateId,
      modelsProductStandardsUpdates: modelsProductStandardsUpdatesValues,
      isSkipCoDecisioning: isSkipCoDecisioning || false,
    }
    let payload = writebackData

    let modifyCertificate = CertificateListService.modifyCertificateV2
    let ipCertificateWriteback = CertificateListService.ipCertificateWritebackV2

    appInsights.trackTrace({
      message: `${logPrefix} - onFinish writeback data - ${JSON.stringify(writebackData)}`,
      severityLevel: SeverityLevel.Information,
    })

    const request = isModifyCert ? modifyCertificate : ipCertificateWriteback

    request(payload).then(async response => {
      if (response?.isSuccessful) {
        if (schemeEcmType === SchemeEcmType.RP)
          await certificateRPSaveAndUploadECM(response.data, isAutoActivated)
        else await certificateSaveAndUploadECM(response.data, isAutoActivated)
      } else {
        showError('Error!', response?.message)
      }
      setIsSavingCertificate(false)
      testReportsValidatorHook.setTestReportMatchedFoundPopupVisible(false)
    })
  }

  const certificateRPSaveAndUploadECM = async (data, isAutoActivated) => {
    data.certificateDetails.isCo = certificateDetailsValues.isCo
    data.certificateDetails.schemeEcmType = schemeEcmType

    const rpAttachmentsFormData = objectToFormData(
      AddAttachmentsRPUtils.convertRPModelToCreateCertificateModel(
        outputAttachmentRPValues,
        data,
        isAutoActivated
      )
    )

    const isEditMode = isEditCert || isModifyCert

    const rpRequestService = isEditCert
      ? CertificateListService.certificateRPUpdateAndUploadECM
      : CertificateListService.certificateRPSaveAndUploadECM

    let response = await rpRequestService(rpAttachmentsFormData)

    // const rpRequestBatchService = isEditCert
    //   ? CertificateListService.certificateRPUpdateAndUploadECMBatch
    //   : CertificateListService.certificateRPSaveAndUploadECMBatch;

    // const rpAttachmentBatches = chunkArray(
    //   outputAttachmentRPValues ?? [],
    //   attachmentUploadBatchSize,
    // );

    // const response =
    //   rpAttachmentBatches.length > 0
    //     ? await rpRequestBatchService(
    //         rpAttachmentBatches.map((rpAttachmentBatch) =>
    //           objectToFormData(
    //             AddAttachmentsRPUtils.convertRPModelToCreateCertificateModel(
    //               rpAttachmentBatch,
    //               data,
    //               isAutoActivated,
    //             ),
    //           ),
    //         ),
    //       )
    //     : await rpRequestService(
    //         objectToFormData(
    //           AddAttachmentsRPUtils.convertRPModelToCreateCertificateModel(
    //             outputAttachmentRPValues,
    //             data,
    //             isAutoActivated,
    //           ),
    //         ),
    //       );

    if (response?.isSuccess) {
      if (isAutoActivated) {
        const withdrawnDate = new Date(data?.certificateDetails?.withdrawnDate)
        const prompt = generatePromptMessage('rp', withdrawnDate, isEditMode)
        const isWithdraw = certificateAction.isWithdraw

        const hasNewlyAddedFiles =
          AddAttachmentsRPUtils.hasNewlyAddedFiles(outputAttachmentRPValues)

        if (!isWithdraw && hasNewlyAddedFiles) {
          showInfo(
            'Uploading Attachments',
            `Certificate is being activated. Uploading attachments
          to the ECM R/P Library and linking to UL Workbench.`
          )
        } else showSuccess(prompt.header, prompt.message)
      } else
        showSuccess(
          'Success!',
          isEditMode ? 'Certificate updated successfully.' : 'Certificate Saved Successfully.'
        )

      refinerEvent.publish({
        certificateNumber: data.certificateDetails.certificateNumber,
      })
    }
    onHide('displayAddNewCertModal', true)
    setOutputAttachmentRPValues([])
    setIsSavingCertificate(false)
  }

  const certificateSaveAndUploadECM = async (data, isAutoActivated) => {
    let model = {}
    let attachments = []
    model = data

    if (outputAttachmentValues) {
      let allAttachments = []
        .concat(...outputAttachmentValues)
        .filter(
          attachment =>
            attachment.isValid == true &&
            attachment.status != CertificateDocumentStatus.TestReportDuplicateLinkToEcmFailed
        )

      let attachmentsForDeletion = []
        .concat(...outputAttachmentValues)
        .filter(attachment => attachment.isValid == false && attachment.id)

      allAttachments = [...allAttachments, ...attachmentsForDeletion]

      allAttachments.forEach(attachment => {
        let obj = {}
        let documentType = attachment.group_index
        obj.id = attachment.id
        obj.file = attachment.file
        obj.documentType = documentType
        obj.status =
          (obj.id == null || obj.id == '') &&
          documentType != CertificateDocumentType.Others &&
          model?.isSaveAndActivate
            ? CertificateDocumentStatus.UploadToEcmInProgress
            : attachment.status
        obj.ecmNodeId = attachment.ecmFileNodeId
        obj.contentType = attachment.ecmContentType
        obj.categoryType = attachment.category
        obj.filename = attachment.name
        obj.isDeleted = attachment.isValid == false && !!attachment.id
        attachments.push(obj)
      })
    }

    model.certificateFiles = attachments

    let upsertCertificateAndUploadECM = CertificateListService.certificateSaveAndUploadECM

    let promptHeader = `Success!`
    let promptMessage = `Certificate saved successfully`

    if (isEditCert) {
      upsertCertificateAndUploadECM = CertificateListService.certificateUpdateAndUploadECM
      promptMessage = `Certificate updated successfully`
    }

    if (isModifyCert) {
      upsertCertificateAndUploadECM = CertificateListService.certificateSaveAndUploadECM
      promptMessage = `Certificate updated successfully`
    }

    const isEditMode = isEditCert || isModifyCert

    if (isAutoActivated) {
      const withdrawnDate = new Date(model?.certificateDetails?.withdrawnDate)
      const prompt = generatePromptMessage('gma', withdrawnDate, isEditMode)
      promptHeader = prompt.header
      promptMessage = prompt.message
    }

    let formData = objectToFormData(model)
    let response = await upsertCertificateAndUploadECM(formData)
    // const upsertCertificateAndUploadECMBatch = isEditCert
    //   ? CertificateListService.certificateUpdateAndUploadECMBatch
    //   : CertificateListService.certificateSaveAndUploadECMBatch;

    // const attachmentBatches = chunkArray(
    //   attachments,
    //   attachmentUploadBatchSize,
    // );

    // const response =
    //   attachmentBatches.length > 0
    //     ? await upsertCertificateAndUploadECMBatch(
    //         attachmentBatches.map((attachmentBatch) =>
    //           objectToFormData({
    //             ...model,
    //             certificateFiles: attachmentBatch,
    //           }),
    //         ),
    //       )
    //     : await upsertCertificateAndUploadECM(objectToFormData(model));

    if (response?.isSuccess) {
      showSuccess(promptHeader, promptMessage)
      refinerEvent.publish({
        certificateNumber: model.certificateDetails.certificateNumber,
      })
    }
    onHide('displayAddNewCertModal', true)
    setIsSavingCertificate(false)
  }

  const isAttachmentAdded = attachments => {
    if (attachments == null || attachments.length == 0) return false

    return attachments[0]?.length > 0 || attachments[1]?.length > 0
  }

  const onClickSave = () => {
    onFinish(false)
  }

  const onClickSaveAndActivate = () => {
    if (outputAttachmentRPValues && outputAttachmentRPValues.length > 0) {
      setIsActivateConfirmDialogShow(true)
    } else if (outputAttachmentValues) {
      const attachments = outputAttachmentValues
      const allAttachments = [].concat(...attachments)
      const hasAttachmentsForLinkAndECM =
        allAttachments.filter(
          file =>
            file[`group_index`] != CertificateDocumentType.Others &&
            (file.status == CertificateDocumentStatus.NotYetUploaded ||
              file.status == CertificateDocumentStatus.UploadToEcmFailed ||
              file.status == CertificateDocumentStatus.ReadyToLinkToWbAndEcmTic ||
              file.status == CertificateDocumentStatus.TestReportDuplicate)
        ).length > 0
      if (hasAttachmentsForLinkAndECM) setIsActivateConfirmDialogShow(true)
      else {
        setTimeout(() => {
          validateTestReport()
        }, 500)
      }
    } else {
      onFinish(true)
    }
  }

  const onClickSaveAndWithdraw = () => {
    setIsWithdrawConfirmDialogShow(true)
  }

  const onCancel = () => {
    hideModal()
  }

  const hideModal = () => {
    if (checkIfHasUnsavedChanges()) {
      setIsCancelConfirmDialogShow(true)
      return
    }
    onHide('displayAddNewCertModal')
  }

  const checkIfHasUnsavedChanges = () => {
    //Note that for ECM, we utilized the outputAttachmentValues and not the productDetailsValues.attachments. Because we cannot store/pass the file object to redux.

    let initialAttachments = initialAttachmentValuesLocal
    let latestAttachments = outputAttachmentValues

    if (isEditCert || isModifyCert) {
      return (
        parseToString(initialProductDetailsValuesLocal?.remarks) !=
          parseToString(productDetailsValues?.remarks) ||
        parseToString(initialProductDetailsValuesLocal?.modelList) !=
          parseToString(productDetailsValues?.modelList) ||
        parseToString(initialAttachments) != parseToString(latestAttachments) ||
        parseToString(initialCertificateDetailFormValuesLocal) !=
          parseToString(certificateDetailFormValues)
      )
    } else {
      return (
        productDetailsValues?.remarks ||
        latestAttachments ||
        productDetailsValues?.modelList ||
        parseToString(certificateDetailFormValues)
      )
    }
  }

  useEffect(() => {
    window.parent.postMessage(
      {
        type: 'UNSAVED_CHANGES',
        value: displayAddNewCertModal,
      },
      '*'
    )
  }, [displayAddNewCertModal])

  const acceptCancelConfirmDialog = () => {
    setIsCancelConfirmDialogShow(false)
    onHide('displayAddNewCertModal')
  }

  const rejectCancelConfirmDialog = () => {
    setIsCancelConfirmDialogShow(false)
  }

  const acceptActivateConfirmDialog = () => {
    setIsActivateConfirmDialogShow(false)
    if (schemeEcmType === SchemeEcmType.RP) {
      onFinish(true)
    } else {
      validateTestReport()
    }
  }

  const rejectActivateConfirmDialog = () => {
    setIsActivateConfirmDialogShow(false)
  }

  const handleSaveAndWithdraw = () => {
    setIsWithdrawConfirmDialogShow(false)
    if (schemeEcmType === SchemeEcmType.RP) {
      onFinish(true)
    } else {
      validateTestReport()
    }
  }

  const handleCancelWithdraw = () => {
    setIsWithdrawConfirmDialogShow(false)
  }

  const onInvalidStepClick = () => {
    if (isModelInRowEditMode || !formAttributesWithError) return

    var fieldIds = Object.entries(formAttributesWithError)
      .filter(filterFieldsWithError => filterFieldsWithError[1])
      .map(field => {
        return `certDetails-${field[0]}`
      })

    if (fieldIds) {
      setCurrentStep(2)
      setScrollIntoBlankRequiredElement(fieldIds)
    }
  }

  const onContinue = () => {
    if (currentStep === 1 && !selectedScheme) {
      setIsDisableContinue(true)
      setIsDisableDraftSave(true)
    } else if (currentStep === 2) {
      setIsLastStepDirty(true)
      saveProjectDetails()
    }

    setCurrentStep(prevState => {
      let newState = prevState
      if (prevState < 4) {
        newState = prevState + 1
      }
      return newState
    })

    topView.scrollIntoView({ behavior: 'smooth' })
  }

  const generatePromptMessage = (library, withdrawnDate, isEditMode) => {
    const isForWithdrawal = withdrawnDate.getFullYear() !== 1 && certificateAction.isWithdraw
    const currentDate = new Date()
    let prompt = {
      header: '',
      message: '',
    }

    if (library === 'gma') {
      if (!isForWithdrawal) {
        prompt.header = 'Success!'
        prompt.message = isEditMode
          ? 'Certificate updated successfully.'
          : 'Certificate successfully created and activated.'
      } else if (withdrawnDate > currentDate) {
        prompt.header = PendingWithdrawalSuccessHeader
        prompt.message = PendingWithdrawalSuccessMessage
      } else if (withdrawnDate <= currentDate) {
        prompt.header = WithdrawalAttachmentUploadingSuccessHeader
        prompt.message = WithdrawalAttachmentUploadingSuccessMessage
      }
    } else if (library === 'rp') {
      if (!isForWithdrawal) {
        prompt.header = 'Success!'
        prompt.message = isEditMode
          ? 'Certificate updated successfully.'
          : 'Certificate successfully created and activated.'
      } else if (withdrawnDate > currentDate) {
        prompt.header = PendingWithdrawalSuccessHeader
        prompt.message = PendingWithdrawalSuccessMessage
      } else if (withdrawnDate <= currentDate) {
        prompt.header = WithdrawalAttachmentUploadingSuccessHeader
        prompt.message = WithdrawalAttachmentUploadingSuccessMessage
      }
    }

    return prompt
  }

  useEffect(() => {
    if (!displayAddNewCertModal) return
    if (
      !productDetailsValues?.attachments &&
      !productDetailsValues?.modelList &&
      !productDetailsValues?.remarks
    )
      return

    if ((isEditCert || isModifyCert) && !initialProductDetailsValuesLocal) {
      setInitialProductDetailsValuesLocal(productDetailsValues)
    }
    setDisableSaveAndActivate(productDetailsValues)
  }, [productDetailsValues, displayAddNewCertModal])

  useEffect(() => {
    if (!displayAddNewCertModal) return
    if (isObjectInvalid(certificateDetailFormValues)) return

    if (
      (isEditCert || isModifyCert) &&
      !initialCertificateDetailFormValuesLocal &&
      certificateDetailFormValues.certificateName
    ) {
      setInitialCertificateDetailFormValuesLocal(certificateDetailFormValues)
    }
  }, [certificateDetailFormValues, displayAddNewCertModal])

  //Note that for ECM, we utilized the outputAttachmentValues and not the productDetailsValues.attachments. Because we cannot store/pass the file object to redux.
  useEffect(() => {
    if (!displayAddNewCertModal) return
    if (!outputAttachmentValues) return
    if ((isEditCert || isModifyCert) && !initialAttachmentValuesLocal) {
      setInitialAttachmentValuesLocal(processInitialAttachments(outputAttachmentValues))
    }
  }, [outputAttachmentValues, displayAddNewCertModal])

  const processInitialAttachments = attachmentValues => {
    //this function is use to recreate the attachment list so it is not pass as a reference object
    const initialAttachments = [].concat(...attachmentValues)
    const certificateAttachments = initialAttachments.filter(
      file => file[`group_index`] == CertificateDocumentType.Certificate
    )

    const testReportAttachments = initialAttachments.filter(
      file => file[`group_index`] == CertificateDocumentType.TestReport
    )

    const othersAttachments = initialAttachments.filter(
      file => file[`group_index`] == CertificateDocumentType.Others
    )

    const attachments = [certificateAttachments, testReportAttachments, othersAttachments]
    return attachments
  }

  const setDisableSaveAndActivate = productDetailsValues => {
    if (
      productDetailsValues &&
      productDetailsValues.modelList &&
      Array.isArray(productDetailsValues.modelList) &&
      productDetailsValues.modelList.length > 0
    ) {
      setIsDisableSaveAndAttach(false)
    } else {
      setIsDisableSaveAndAttach(true)
    }
  }

  useEffect(() => {
    if (passedCurrentStep) {
      setCurrentStep(passedCurrentStep)
    }
  }, [passedCurrentStep])

  const getSchemeList = async function () {
    const result = await CertificateListService.getCertificateScheme()
    const data = result.data
    if (data && Array.isArray(data) && data.length > 0) {
      setSchemeList(data)
    }
  }

  const saveProjectDetails = () => {
    ProjectDetailsService.saveProjectDetail({
      projectLineDetail: projectDetailsValues.selectedProjectDetails,
    })
  }

  const isModifyOrEditCertificate =
    (isEditCert || isModifyCert) && (currentStep === 2 || currentStep === 3)

  const isSaveAndActivateDisabled =
    !isValidStep[3] ||
    isDisableSaveAndAttach ||
    isSavingCertificate ||
    !isAttachmentsValid ||
    (schemeEcmType === SchemeEcmType.RP && !isRPAttachmentValid)

  const customizedMarker = item => {
    const isFormInvalid =
      item.number === 2 &&
      currentStep >= item.number &&
      !isValidStep[3] &&
      (isEditCert || isModifyCert || isLastStepDirty)

    return (
      <>
        <Badge
          value={
            isFormInvalid ? (
              <FontAwesomeIcon
                className={isModelInRowEditMode ? '' : classes['invalidStep__button']}
                icon={faExclamation}
                onClick={onInvalidStepClick}
              />
            ) : item.number < currentStep ? (
              <FontAwesomeIcon icon={faCheck} />
            ) : (
              item.number
            )
          }
          style={{
            backgroundColor: isFormInvalid
              ? invalidStepBackgroundColor
              : item.number <= currentStep
                ? steppedBackgroundColor
                : unSteppedBackgroundColor,
            color: isFormInvalid
              ? invalidStepTextColor
              : item.number <= currentStep
                ? steppedTextColor
                : unSteppedTextColor,
            borderRadius: '50%',
            opacity: item.number === 2 && isModelInRowEditMode && !isValidStep[3] ? 0.5 : 1,
          }}
          size="xlarge"
        ></Badge>
      </>
    )
  }

  const renderFooter = () => {
    const saveActiveLabel = certificateAction.isWithdraw ? 'SAVE AND WITHDRAW' : 'SAVE AND ACTIVATE'
    const saveActiveAction = certificateAction.isWithdraw
      ? onClickSaveAndWithdraw
      : onClickSaveAndActivate

    const disabledActivateToolTip = certificateAction.isWithdraw
      ? 'Please fill out all required fields to enable this.'
      : 'Please fill out all required fields and link at least one model to enable activation.'

    const isWithdrawValidToSave = SaveWithdrawValidation('certificate_revisionNumber')
    const isSaveWithdrawDisabled = certificateAction.isWithdraw && !isWithdrawValidToSave

    const isSaveDisabled =
      isSavingCertificate ||
      !isAttachmentsValid ||
      (schemeEcmType === SchemeEcmType.RP && !isRPAttachmentValid) ||
      isSaveWithdrawDisabled

    const isProjectSelected = Boolean(
      projectDetailsValues.selectedProjectDetails && projectDetailsValues.orderLineNumbers
    )

    const footerDisabled =
      footerActionsDisabled ||
      (Object.values(formValidityMessages).some(item => Boolean(item.displayOnEffect)) &&
        currentStep !== 1)

    const renderStepActions = () => {
      if (currentStep === 1) {
        return (
          <button
            className="ul-button -app-primary -medium -icon add-new-cert-continue-btn"
            onClick={onContinue}
            disabled={!isProjectSelected}
          >
            <span>CONTINUE&nbsp;</span>
            <FontAwesomeIcon icon={faAnglesRight} />
          </button>
        )
      }

      if (currentStep === 2) {
        return (
          <>
            {!certificateAction.isWithdraw && (
              <button
                className="ul-button -medium -icon add-new-cert-save-btn"
                onClick={onClickSave}
                disabled={isDisableDraftSave}
              >
                SAVE
              </button>
            )}
            <button
              className="ul-button -app-primary -medium -icon add-new-cert-continue-btn"
              onClick={onContinue}
              disabled={isDisableContinue}
            >
              <span>CONTINUE&nbsp;</span>
              <FontAwesomeIcon icon={faAnglesRight} />
            </button>
          </>
        )
      }

      if (currentStep === 3) {
        return (
          <>
            <button
              className="ul-button -medium -icon add-new-cert-save-btn"
              onClick={onClickSave}
              disabled={isSaveDisabled}
              title={isSaveDisabled ? disabledActivateToolTip : ''}
            >
              SAVE
            </button>

            <button
              className="ul-button -medium -icon add-new-cert-save-activate-btn"
              onClick={saveActiveAction}
              disabled={isSaveAndActivateDisabled}
              title={isSaveAndActivateDisabled ? disabledActivateToolTip : ''}
            >
              {saveActiveLabel}
            </button>
          </>
        )
      }

      return null
    }

    return (
      <div className={footerDisabled ? classes['footer--disabled'] : ''}>
        <hr className="modal-footer-hr" />
        <div className="add-new-certificate-container pb-2">
          {currentStep !== 1 && (
            <button
              className="ul-button -app-secondary -medium -icon float-left add-new-cert-back-btn"
              onClick={onBack}
              disabled={isModelOnEdit}
            >
              <FontAwesomeIcon icon={faAnglesLeft} />
              <span>&nbsp;BACK</span>
            </button>
          )}
          <button
            className="ul-button -medium -icon add-new-cert-btn-cancel"
            onClick={onCancel}
            disabled={isSavingCertificate}
          >
            <span>CANCEL</span>
          </button>
          {renderStepActions()}
        </div>
      </div>
    )
  }

  const onCloseModal = () => {
    hideModal()
  }

  const getHeader = () => {
    if (isEditCert || isModifyCert) return customModalHeader
    return modalHeader[currentStep]
  }

  const confirmCancelDialogBody = () => {
    return (
      <>
        <div className="custom-confirm-dialog-body">
          <div>You have updated information that has not been saved.</div>
          <div className="custom-space-below">Any unsaved changes will be lost.</div>
          <div>Would you like to Continue?</div>
        </div>
      </>
    )
  }

  const confirmCancelFooter = () => {
    return (
      <>
        <div className="custom-confirm-dialog-footer">
          <button
            className="ul-button -app-primary -medium custom-confirm-dialog-accept-btn"
            onClick={acceptCancelConfirmDialog}
          >
            YES
          </button>
          <button
            className="ul-button -medium default-cancel-button custom-confirm-dialog-reject-btn"
            onClick={rejectCancelConfirmDialog}
          >
            NO
          </button>
        </div>
      </>
    )
  }

  const confirmActivateDialogBody = () => {
    return (
      <>
        <div className="custom-confirm-dialog-body">
          <div className="custom-space-below">
            File(s) will be saved to ECM TIC and cannot be deleted.
          </div>
          <div>Would you like to Continue?</div>
        </div>
      </>
    )
  }

  const confirmActivateFooter = () => {
    return (
      <>
        <div className="custom-confirm-dialog-footer">
          <button
            className="ul-button -app-primary -medium custom-confirm-dialog-accept-btn"
            onClick={acceptActivateConfirmDialog}
          >
            YES
          </button>
          <button
            className="ul-button -medium default-cancel-button custom-confirm-dialog-reject-btn"
            onClick={rejectActivateConfirmDialog}
          >
            NO
          </button>
        </div>
      </>
    )
  }

  const confirmWithdrawDialogBody = () => {
    return (
      <>
        <div className="custom-confirm-dialog-body">
          <div className="custom-space-below">
            Please review the following before proceeding:
            <ul>
              <li>Any newly added files will be saved to EMC TIC.</li>
              <li>
                When Withdrawing a Certificate: Once the certificate is withdrawn, any unlinked
                files will remain unlinked from UL Workbench and will still be retained in ECM TIC.
              </li>
            </ul>
          </div>
          <div>Would you like to continue?</div>
        </div>
      </>
    )
  }

  const confirmwithdrawFooter = () => {
    return (
      <>
        <div className="custom-confirm-dialog-footer">
          <button
            className="ul-button -app-primary -medium custom-confirm-dialog-accept-btn"
            onClick={handleSaveAndWithdraw}
          >
            YES
          </button>
          <button
            className="ul-button -medium default-cancel-button custom-confirm-dialog-reject-btn"
            onClick={handleCancelWithdraw}
          >
            NO
          </button>
        </div>
      </>
    )
  }

  const headerIconsTemplate = () => {
    return (
      <>
        <ExpandIcon
          className={classes['modal-expand-icon']}
          onExpand={() => setDialogMaximized(true)}
          onCollapse={() => setDialogMaximized(false)}
          expanded={dialogMaximized}
        />
        <CloseIcon onCloseEvent={onCloseModal} />
      </>
    )
  }

  const getDialogStyle = () => {
    if (dialogMaximized) {
      return { width: '100vw', height: '100vh' }
    }

    return { width: '90vw', height: '90vh' }
  }

  const handleOnProceedWithoutDuplicate = () => {
    testReportsValidatorHook.setTestReportMatchedFoundPopupVisible(false)

    const newOutputAttachmentValues =
      testReportsValidatorHook.removeDuplicateTestReportAttachments(outputAttachmentValues)

    setOutputAttachmentValues(newOutputAttachmentValues)
    onFinish(true)
  }

  const certificateLoader = message => (
    <div className="loading-cert-attr-div">
      <div>
        <div className="fetching-attr-load-label">{message}</div>
        <div>
          <svg className="ul-progressCircle -medium">
            <circle className="ul-progressCircle__outer" cx="50%" cy="50%" r="35%" />
            <circle className="ul-progressCircle__inner" cx="50%" cy="50%" r="35%" />
          </svg>
        </div>
      </div>
    </div>
  )

  const SaveWithdrawValidation = field => {
    const flatAttributeValue = certificateFlatAttributes?.find(
      attributes => attributes.attribute_unique_name === field && attributes.display !== 'N'
    )

    if (!flatAttributeValue) return true

    const fieldValue = certificateDetailFormValues[field] ?? ''
    return fieldValue !== flatAttributeValue?.value
  }

  return (
    <>
      <Dialog
        draggable={false}
        header={getHeader()}
        visible={displayAddNewCertModal}
        style={getDialogStyle()}
        footer={renderFooter('displayAddNewCertModal')}
        onHide={() => onHide('displayAddNewCertModal')}
        icons={headerIconsTemplate()}
        className={`add-new-cert-dialog ${
          dialogMaximized ? classes['expanded'] : classes['collapsed']
        }`}
        closable={false}
      >
        <hr className={`modal-header-hr ${classes[`modal-header-hr`]}`} />
        <div className="add-new-cert-timeline">
          <Timeline
            value={modalSteps}
            align="top"
            layout="horizontal"
            className="customized-timeline"
            marker={customizedMarker}
          />
        </div>
        <div className="add-new-cert-modal-body">
          {testReportsValidatorHook.isValidating &&
            certificateLoader('Validating duplicate Report(s)...')}

          {isSavingCertificate && certificateLoader('Saving certificate...')}

          <div
            className={clsx(
              (testReportsValidatorHook.isValidating || isSavingCertificate) && 'display-none',
              classes['body-content']
            )}
          >
            <div
              ref={el => {
                topView = el
              }}
            ></div>
            <div className={currentStep !== 1 ? 'display-none' : 'add-new-cert-modal-body-content'}>
              <ProjectOrderDetails
                certificateId={certificateId}
                certificateScheme={certificateScheme}
                localData={localData}
                isEditCert={isEditCert}
                isModifyCert={isModifyCert}
                setIsDisableContinue={setIsDisableContinue}
                isCopyCertificate={isCopyCertificate}
                setSelectedScheme={setSelectedScheme}
                setInitialProductDetailsValuesLocal={setInitialProductDetailsValuesLocal}
                setInitialCertificateDetailFormValuesLocal={
                  setInitialCertificateDetailFormValuesLocal
                }
                setInitialAttachmentValuesLocal={setInitialAttachmentValuesLocal}
                setIsDisableSaveAndAttach={setIsDisableSaveAndAttach}
                setIsLastStepDirty={setIsLastStepDirty}
                setIsShowCertSchemeDropdown={setIsShowCertSchemeDropdown}
                setOutputAttachmentRPValues={setOutputAttachmentRPValues}
                certificateLoader={certificateLoader}
              />
            </div>
            <div className={currentStep !== 2 ? 'display-none' : 'add-new-cert-modal-body-content'}>
              <AddNewCertDetails
                onContinue={onContinue}
                schemeList={schemeList}
                setIsValidStep={setIsValidStep}
                isEditCert={isEditCert}
                isModifyCert={isModifyCert}
                hierarchyId={hierarchyId}
                editData={editData}
                setFormValues={setFormValues}
                formValues={formValues}
                hasRevisions={hasRevisions}
                setIsDisableContinue={setIsDisableContinue}
                certificateStatus={certificateStatus}
                formAttributesWithError={formAttributesWithError}
                setFormAttributesWithError={setFormAttributesWithError}
                formValidityMessages={formValidityMessages}
                setFormValidityMessages={setFormValidityMessages}
                scrollIntoBlankRequiredElement={scrollIntoBlankRequiredElement}
                setScrollIntoBlankRequiredElement={setScrollIntoBlankRequiredElement}
                isCertificateSchemeUpdatable={!hasCertificateBeenActive}
                currentStep={currentStep}
                setIsCertificateHaveStandard={setIsCertificateHaveStandard}
                selectedScheme={selectedScheme}
                setSelectedScheme={setSelectedScheme}
                isLastStepDirty={isLastStepDirty}
                setIsLastStepDirty={setIsLastStepDirty}
                isCopyCertificate={isCopyCertificate}
                certificateId={certificateId}
                certificateNumber={certificateNumber}
                setCertificateNumber={setCertificateNumber}
                isAutoGenerate={isAutoGenerate}
                setIsAutoGenerate={setIsAutoGenerate}
                isSavingCertificate={isSavingCertificate}
                setIsModelInRowEditMode={setIsModelInRowEditMode}
                setIsShowCertSchemeDropdown={setIsShowCertSchemeDropdown}
                isShowCertSchemeDropdown={isShowCertSchemeDropdown}
                setIsDisableDraftSave={setIsDisableDraftSave}
                certificateLoader={certificateLoader}
              />
            </div>
            <div className={currentStep !== 3 ? 'display-none' : 'add-new-cert-modal-body-content'}>
              <AddNewCertProdDetails
                certificateId={certificateId}
                setFormValues={setFormValues}
                isSavingCertificate={isSavingCertificate}
                isEditCert={isEditCert}
                isModifyCert={isModifyCert}
                setOutputAttachmentValues={setOutputAttachmentValues}
                onSetRowEditMode={setFooterActionsDisabled}
                currentStep={currentStep}
                setIsModelInRowEditMode={setIsModelInRowEditMode}
                formValidityMessages={formValidityMessages}
                setFormValidityMessages={setFormValidityMessages}
                inputAttachments={inputAttachments}
                setAttachments={setAttachments}
                isCertificateHaveStandard={isCertificateHaveStandard}
                testReportsValidatorHook={testReportsValidatorHook}
                setIsAttachmentsValid={setIsAttachmentsValid}
                projectDetailsValues={projectDetailsValues}
                hasRevisions={hasRevisions}
                rpAttachments={outputAttachmentRPValues}
                setRPAttachments={setOutputAttachmentRPValues}
                updateRPAttachmentOnParent={updateRPAttachmentOnParent}
                uniqueCertificateId={uniqueCertificateId}
                isAutoGenerate={isAutoGenerate}
                certificateNumber={certificateNumber}
              />
            </div>
          </div>
        </div>
      </Dialog>
      <ConfirmDialog
        style={{ width: '600px' }}
        className="custom-confirm-dialog"
        visible={isCancelConfirmDialogShow}
        header="Unsaved Changes Confirmation"
        message={confirmCancelDialogBody}
        footer={confirmCancelFooter}
        onHide={() => rejectCancelConfirmDialog()}
      />
      <ConfirmDialog
        style={{ width: '520px' }}
        className="custom-confirm-dialog"
        visible={isActivateConfirmDialogShow}
        header="Save and Activate Confirmation"
        message={confirmActivateDialogBody}
        footer={confirmActivateFooter}
        onHide={() => rejectActivateConfirmDialog()}
      />
      <ConfirmDialog
        style={{ width: '650px' }}
        className="custom-confirm-dialog"
        visible={isWithdrawConfirmDialogShow}
        header="Save and Activate / Withdraw Confirmation"
        message={confirmWithdrawDialogBody}
        footer={confirmwithdrawFooter}
        onHide={() => handleCancelWithdraw()}
      />

      <TestReportMatchedFoundPopup
        visible={testReportsValidatorHook.testReportMatchedFoundPopupVisible}
        onProceedWithoutDuplicate={() => {
          testReportsValidatorHook.setTestReportMatchedFoundPopupVisible(false)
        }}
        onProceedWithDuplicate={() => {
          onFinish(true)
        }}
        onClose={() => {
          testReportsValidatorHook.setTestReportMatchedFoundPopupVisible(false)
        }}
        rows={testReportRows}
      />
    </>
  )
}

export default AddNewCertificate
